import { useEffect, useState } from 'react'
import { getEffectiveTmdbApiKey, useUserSettings } from '../settings/useUserSettings'
import {
  fetchMovieDetails,
  fetchSeriesDetails,
  type TmdbMovieDetails,
  type TmdbSeriesDetails,
} from '../tmdb/tmdb'
import { LazyImage } from '../ui/LazyImage'

/**
 * Featured hero row at the top of the home view. Shows the single most
 * recent in-progress movie AND the single most recent in-progress
 * series side by side, so the user can hop back into either. No
 * continue-watching carousel underneath — that was removed per the
 * user's explicit request ("only the element with last movie and last
 * series").
 *
 * Layout:
 *   - both present → two cards side by side (stacks on narrow viewports)
 *   - only one     → that one spans the full width
 *   - neither      → WelcomeHero with a "Start searching" CTA
 */

export interface HeroMovieSeed {
  tmdbId: number
  progressSeconds: number
}

export interface HeroSeriesSeed {
  tmdbId: number
  lastSeason: number | null
  lastEpisode: number | null
}

interface Props {
  lastMovie: HeroMovieSeed | null
  lastSeries: HeroSeriesSeed | null
  onOpenDetail: (tmdbId: number, type: 'movie' | 'tv') => void
  hasLibrary: boolean
  onOpenSearch: () => void
}

export function HomeHero({
  lastMovie,
  lastSeries,
  onOpenDetail,
  hasLibrary,
  onOpenSearch,
}: Props) {
  const { settings } = useUserSettings()
  // Falls back to the bundled developer key when the user hasn't supplied one.
  const apiKey = getEffectiveTmdbApiKey(settings)
  const language = settings?.tmdbPreferredLanguage || 'en'

  const [movieDetails, setMovieDetails] = useState<TmdbMovieDetails | null>(null)
  const [seriesDetails, setSeriesDetails] = useState<TmdbSeriesDetails | null>(
    null
  )

  useEffect(() => {
    if (!apiKey || !lastMovie) {
      setMovieDetails(null)
      return
    }
    let cancelled = false
    fetchMovieDetails(lastMovie.tmdbId, apiKey, language).then((d) => {
      if (!cancelled) setMovieDetails(d)
    })
    return () => {
      cancelled = true
    }
  }, [lastMovie, apiKey, language])

  useEffect(() => {
    if (!apiKey || !lastSeries) {
      setSeriesDetails(null)
      return
    }
    let cancelled = false
    fetchSeriesDetails(lastSeries.tmdbId, apiKey, language).then((d) => {
      if (!cancelled) setSeriesDetails(d)
    })
    return () => {
      cancelled = true
    }
  }, [lastSeries, apiKey, language])

  const bothEmpty = !lastMovie && !lastSeries

  if (bothEmpty) {
    if (!hasLibrary) return <WelcomeHero onOpenSearch={onOpenSearch} />
    // Library exists but nothing in progress — minimal CTA so the page
    // still has an anchor above the fold.
    return <ResumeEmptyHero onOpenSearch={onOpenSearch} />
  }

  const movieCard =
    lastMovie && movieDetails ? (
      <HeroCard
        title={movieDetails.title}
        year={movieDetails.year}
        rating={movieDetails.rating}
        backdropUrl={movieDetails.backdropUrl}
        posterUrl={movieDetails.posterUrl}
        tagline={
          (() => {
            const runtimeSeconds = (movieDetails.runtime ?? 0) * 60
            return formatTimeLeft(runtimeSeconds - lastMovie.progressSeconds)
          })() || 'In progress'
        }
        progressPct={(() => {
          const runtimeSeconds = (movieDetails.runtime ?? 0) * 60
          return runtimeSeconds
            ? Math.min(
                100,
                Math.round((lastMovie.progressSeconds / runtimeSeconds) * 100)
              )
            : null
        })()}
        label="Last movie"
        onClick={() => onOpenDetail(movieDetails.tmdbId, 'movie')}
      />
    ) : lastMovie ? (
      <HeroCardSkeleton />
    ) : null

  const seriesCard =
    lastSeries && seriesDetails ? (
      <HeroCard
        title={seriesDetails.name}
        year={seriesDetails.year}
        rating={seriesDetails.rating}
        backdropUrl={seriesDetails.backdropUrl}
        posterUrl={seriesDetails.posterUrl}
        tagline={
          lastSeries.lastSeason != null && lastSeries.lastEpisode != null
            ? `Last watched · S${lastSeries.lastSeason}E${lastSeries.lastEpisode}`
            : 'In progress'
        }
        progressPct={null}
        label="Last series"
        onClick={() => onOpenDetail(seriesDetails.tmdbId, 'tv')}
      />
    ) : lastSeries ? (
      <HeroCardSkeleton />
    ) : null

  return (
    <section className="mx-auto w-full max-w-screen-xl px-4">
      <div className="grid gap-4 md:grid-cols-2">
        {movieCard}
        {seriesCard}
      </div>
    </section>
  )
}

// ────────────────────────────────────────────────────────────────────
// Card shell
// ────────────────────────────────────────────────────────────────────

function HeroCard({
  label,
  title,
  year,
  rating,
  tagline,
  backdropUrl,
  posterUrl,
  progressPct,
  onClick,
}: {
  label: string
  title: string
  year: number | null
  rating: number
  tagline: string
  backdropUrl: string | null
  posterUrl: string | null
  progressPct: number | null
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative block w-full overflow-hidden rounded-2xl border border-border-primary bg-bg-secondary/80 text-left shadow-cinematic ring-1 ring-white/5 transition-all duration-500 ease-cinematic hover:ring-accent-primary/40 hover:shadow-cinematic-lg"
      style={{ minHeight: 'clamp(200px, 22vw, 280px)' }}
    >
      {backdropUrl ? (
        <div className="absolute inset-0">
          <LazyImage
            src={backdropUrl}
            alt=""
            className="h-full w-full object-cover"
          />
        </div>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-bg-tertiary via-bg-secondary to-bg-primary" />
      )}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-bg-primary/95 via-bg-primary/70 to-transparent" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-bg-primary/80 via-transparent to-transparent" />

      <div
        className="relative z-10 flex h-full items-end gap-3 p-4 sm:p-5"
        style={{ minHeight: 'clamp(200px, 22vw, 280px)' }}
      >
        {posterUrl && (
          <div
            className="hidden shrink-0 overflow-hidden rounded-lg border border-white/10 shadow-cinematic sm:block"
            style={{ width: 'clamp(80px, 11vw, 120px)' }}
          >
            <div className="aspect-[2/3] w-full">
              <LazyImage
                src={posterUrl}
                alt=""
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          <span className="mb-1 text-[10px] font-bold uppercase tracking-widest text-accent-primary/90 sm:text-xs">
            {label}
          </span>
          <h2
            className="line-clamp-2 font-bold leading-tight text-white drop-shadow-lg"
            style={{ fontSize: 'clamp(1.25rem, 2.4vw, 2rem)' }}
          >
            {title}
          </h2>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-text-secondary">
            {year != null && <span>{year}</span>}
            {rating > 0 && (
              <>
                <Dot />
                <span className="flex items-center gap-1 text-blue-300">
                  ★ {rating.toFixed(1)}
                </span>
              </>
            )}
            <Dot />
            <span className="font-medium text-accent-primary">{tagline}</span>
          </div>

          <div className="mt-3 inline-flex items-center gap-2 self-start rounded-lg bg-accent-primary px-3 py-1.5 text-xs font-semibold text-white shadow-cinematic transition-transform duration-300 ease-cinematic group-hover:translate-x-0.5">
            <PlayIcon />
            Resume
          </div>

          {progressPct != null && (
            <div className="relative mt-3 h-1 w-full max-w-[220px] overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-accent-primary shadow-[0_0_10px_rgba(0,212,170,0.5)] transition-all duration-500 ease-out"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          )}
        </div>
      </div>
    </button>
  )
}

function HeroCardSkeleton() {
  return (
    <div
      className="animate-pulse overflow-hidden rounded-2xl border border-border-primary bg-gradient-to-br from-bg-secondary to-bg-tertiary"
      style={{ minHeight: 'clamp(200px, 22vw, 280px)' }}
    />
  )
}

// ────────────────────────────────────────────────────────────────────
// Empty states
// ────────────────────────────────────────────────────────────────────

function WelcomeHero({ onOpenSearch }: { onOpenSearch: () => void }) {
  return (
    <section className="relative mx-auto w-full max-w-screen-xl px-4">
      <div
        className="relative overflow-hidden rounded-2xl border border-border-primary bg-gradient-to-br from-bg-tertiary via-bg-secondary to-bg-primary p-6 shadow-cinematic sm:p-10"
        style={{ minHeight: 'clamp(200px, 26vw, 320px)' }}
      >
        <div className="relative z-10 flex h-full max-w-2xl flex-col justify-center">
          <span className="mb-2 text-[10px] font-bold uppercase tracking-widest text-accent-primary/90 sm:text-xs">
            Welcome to Govu
          </span>
          <h2
            className="font-bold leading-tight text-white drop-shadow-lg"
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2.5rem)' }}
          >
            Your movie library, everywhere.
          </h2>
          <p className="mt-3 max-w-xl text-sm text-text-secondary sm:text-base">
            Start by searching for a movie or series you love. Rate what you've
            seen and Govu will build personal recommendations just for you.
          </p>
          <button
            type="button"
            onClick={onOpenSearch}
            className="mt-5 inline-flex items-center gap-2 self-start rounded-lg bg-accent-primary px-4 py-2 text-xs font-semibold text-white shadow-cinematic transition-transform duration-300 ease-cinematic hover:translate-x-0.5 sm:text-sm"
          >
            <SearchIcon />
            Start searching
          </button>
        </div>
      </div>
    </section>
  )
}

function ResumeEmptyHero({ onOpenSearch }: { onOpenSearch: () => void }) {
  return (
    <section className="relative mx-auto w-full max-w-screen-xl px-4">
      <div
        className="relative overflow-hidden rounded-2xl border border-border-primary bg-gradient-to-br from-bg-secondary to-bg-tertiary p-6 shadow-cinematic sm:p-8"
        style={{ minHeight: 'clamp(160px, 18vw, 220px)' }}
      >
        <div className="relative z-10 flex h-full max-w-xl flex-col justify-center">
          <span className="mb-1 text-[10px] font-bold uppercase tracking-widest text-text-tertiary sm:text-xs">
            Nothing in progress
          </span>
          <h2
            className="font-bold leading-tight text-white drop-shadow-lg"
            style={{ fontSize: 'clamp(1.25rem, 2.4vw, 2rem)' }}
          >
            Pick up something new.
          </h2>
          <button
            type="button"
            onClick={onOpenSearch}
            className="mt-4 inline-flex items-center gap-2 self-start rounded-lg bg-accent-primary px-3 py-1.5 text-xs font-semibold text-white shadow-cinematic hover:translate-x-0.5"
          >
            <SearchIcon />
            Search
          </button>
        </div>
      </div>
    </section>
  )
}

// ────────────────────────────────────────────────────────────────────
// Icons & helpers
// ────────────────────────────────────────────────────────────────────

function Dot() {
  return <span className="h-1 w-1 shrink-0 rounded-full bg-white/30" aria-hidden />
}

function PlayIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M8 5v14l11-7z" />
    </svg>
  )
}

function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}

function formatTimeLeft(remainingSeconds: number): string {
  if (remainingSeconds <= 0) return ''
  if (remainingSeconds < 60) return 'Less than 1m left'
  const hours = Math.floor(remainingSeconds / 3600)
  const minutes = Math.round((remainingSeconds % 3600) / 60)
  if (hours > 0) {
    if (minutes >= 60) return `${hours + 1}h left`
    if (minutes === 0) return `${hours}h left`
    return `${hours}h ${minutes}m left`
  }
  return `${Math.max(1, minutes)}m left`
}
